import axios from 'axios'

import { Message } from 'element-ui'
import { getRefreshToken, saveTokens, clearLoginSession } from './auth.js'
import { rewriteOriginUrls } from './originRewrite.js'
import { safeParseJson } from './bigintSafe.js'

const http = axios.create({
	baseURL: process.env.VUE_APP_BASE_API,
	timeout: 1000 * 30,
	withCredentials: true,
	// 雪花ID防精度丢失: 默认 JSON.parse 会截断19位长整型, 改用安全解析
	transformResponse: [function (data) {
		return safeParseJson(data);
	}]
})

// Token刷新并发锁: 多个请求同时401时只发一次刷新
let refreshTokenPromise = null

/**
 * 请求拦截
 */
http.interceptors.request.use(config => {
	let accessToken = sessionStorage.getItem("accessToken");
	if (accessToken) {
		config.headers.accessToken = encodeURIComponent(accessToken);
	}
	return config
}, error => {
	return Promise.reject(error)
})

/**
 * 响应拦截
 */
http.interceptors.response.use(async response => {
	// 库里存的图片/文件URL写死了域名，纯IP应急通道下加载不了，统一改写成当前origin。
	// 例外：上传类接口。它们的返回值会被原样写回服务端（消息content、头像等），
	// 一旦改写成相对路径就以相对路径存库——App端没有origin兜底，图片全部显示不出来
	// （2026-08-10 查实：08-08 上线本改写后，web端发出的图片消息全部存成相对路径，
	// 安卓/iOS 看不到别人发的图片，根因即此）
	const reqUrl = (response.config && response.config.url) || '';
	if (reqUrl.indexOf('upload') === -1) {
		rewriteOriginUrls(response.data);
	}
	if (response.data.code == 200) {
		return response.data.data;
	} else if (response.data.code == 400) {
		location.href = location.pathname + "#/login";
		return Promise.reject(response.data);
	} else if (response.data.code == 401) {
		console.log("token失效，尝试重新获取")
		let refreshToken = getRefreshToken();
		if (!refreshToken) {
			location.href = location.pathname + "#/login";
			return Promise.reject(response.data);
		}
		// 并发刷新保护: 多个请求同时401时复用同一个刷新Promise
		if (!refreshTokenPromise) {
			refreshTokenPromise = http({
				method: 'put',
				url: '/refreshToken',
				headers: {
					refreshToken: refreshToken
				}
			}).then(data => {
				saveTokens(data);
				refreshTokenPromise = null;
				return data;
			}).catch(() => {
				refreshTokenPromise = null;
				// 清除残留的旧 token，避免带着失效 token 反复触发刷新
				clearLoginSession();
				location.href = location.pathname + "#/login";
				return Promise.reject(response.data);
			})
		}
		const data = await refreshTokenPromise;
		response.config.headers.accessToken = encodeURIComponent(data.accessToken);
		// 重新发送刚才的请求
		return http(response.config)
	} else {
		Message({
			message: response.data.message,
			type: 'error',
			duration: 1500,
			customClass: 'element-error-message-zindex'
		})
		return Promise.reject(response.data)
	}
}, error => {
	// 网络层失败/超时没有 response, 直接读 status 会抛 TypeError(表现为莫名其妙的
	// "Cannot read properties of undefined (reading 'status')" 弹窗), 统一兑底成可读提示
	const resp = error.response;
	if (!resp) {
		Message({
			message: '网络请求失败或超时，请检查网络后重试',
			type: 'error',
			duration: 1500,
			customClass: 'element-error-message-zindex'
		})
		return Promise.reject(new Error('网络请求失败或超时，请检查网络后重试'))
	}
	switch (resp.status) {
		case 400:
			Message({
				message: resp.data,
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break
		case 401:
			location.href = location.pathname + "#/login";
			break
		case 405:
			Message({
				message: 'http请求方式有误',
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break
		case 404:
		case 500:
			Message({
				message: '服务器出了点小差，请稍后再试',
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break
		case 501:
			Message({
				message: '服务器不支持当前请求所需要的某个功能',
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break
	}

	return Promise.reject(error)
})


export default http
